import { useTranslation } from 'react-i18next';
import type { BLOG_POSTS } from '@/mocks/blog';
import type { BORGHI } from '@/mocks/borghi';
import {
  Lang,
  intro,
  qualitaTitle,
  qualitaIntro,
  qualitaAscoli,
  qualitaMacerata,
  qualitaAncona,
  qualitaPesaro,
  qualitaFermo,
  qualitaConclusion,
  costiTitle,
  costiIntro,
  costiCasa,
  costiAffitti,
  costiUtenze,
  sanitaTitle,
  sanitaText,
  trasportiTitle,
  trasportiText,
  chiTitle,
  chiIntro,
  chiNazionalita,
  chiProvince,
  chiInterno,
  puntiTitle,
  puntiIntro,
  puntiCards,
  criticitaTitle,
  criticitaIntro,
  criticitaCards,
  opportunitaTitle,
  opportunitaIntro,
  opportunitaCards,
  conclusioneTitle,
  conclusionePara1,
  conclusionePara2,
  conclusionePara3,
  conclusionePara4,
  conclusioneQuote,
  fonti,
  labels,
  provinceData,
  borgoLinks,
} from './VivereMarcheContent';

type PostType = typeof BLOG_POSTS[0];
type BorgoType = typeof BORGHI[0];

interface Props {
  post: PostType;
  lang: Lang;
  relatedBorghiData: BorgoType[];
}

const VivereMarcheBody = ({ lang, relatedBorghiData }: Props) => {
  const { t } = useTranslation();

  const getBorgoTagline = (b: BorgoType) => {
    if (lang === 'en') return b.taglineEn;
    if (lang === 'de') return b.taglineDe;
    return b.tagline;
  };

  const sectionTitle = (id: string, text: string) => (
    <h4 id={id} className="font-serif text-2xl text-anthracite-700 font-light pt-4">
      <a href={`#${id}`} className="hover:text-terracotta-300 transition-colors">
        {text}
      </a>
    </h4>
  );

  const accentClass = (accent: string, type: 'bg' | 'border' | 'text' | 'circle') => {
    if (type === 'bg') {
      return accent === 'terracotta' ? 'bg-terracotta-50 border-terracotta-200' :
        accent === 'olive' ? 'bg-olive-50 border-olive-200' :
        'bg-ivory-100 border-gold-200';
    }
    if (type === 'text') {
      return accent === 'terracotta' ? 'text-terracotta-500' :
        accent === 'olive' ? 'text-olive-500' :
        'text-gold-400';
    }
    return accent === 'terracotta' ? 'bg-terracotta-100' :
      accent === 'olive' ? 'bg-olive-100' :
      'bg-gold-100';
  };

  return (
    <div className="space-y-6">
      {/* ── INTRO ── */}
      <p className="font-sans text-lg text-anthracite-500 leading-relaxed mb-8 border-l-4 border-terracotta-300 pl-6">
        {intro[lang]}
      </p>

      {/* ── Section 1: Qualità della vita ── */}
      {sectionTitle('qualita-vita-numeri', qualitaTitle[lang])}
      <p className="font-sans text-base text-anthracite-500 leading-relaxed">{qualitaIntro[lang]}</p>

      <div className="space-y-4 mt-4">
        {provinceData.map((p) => {
          const bodyMap: Record<string, string> = {
            'Ascoli Piceno': qualitaAscoli[lang],
            'Macerata': qualitaMacerata[lang],
            'Ancona': qualitaAncona[lang],
            'Pesaro-Urbino': qualitaPesaro[lang],
            'Fermo': qualitaFermo[lang],
          };
          return (
            <div key={p.title} className={`rounded-xl p-5 ${accentClass(p.accent, 'bg')}`}>
              <div className="flex items-center justify-between mb-2">
                <p className={`font-serif text-lg font-medium ${accentClass(p.accent, 'text')}`}>
                  {labels.categoria[lang]} {p.title}
                </p>
                <span className="font-sans text-xs tracking-widest uppercase text-anthracite-400 bg-white/60 border border-anthracite-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                  {p.pos}
                </span>
              </div>
              <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{bodyMap[p.title]}</p>
            </div>
          );
        })}
      </div>

      <p className="font-sans text-base text-anthracite-500 leading-relaxed mt-4">{qualitaConclusion[lang]}</p>

      {/* ── Section 2: Costo della vita ── */}
      {sectionTitle('costo-della-vita', costiTitle[lang])}
      <p className="font-sans text-base text-anthracite-500 leading-relaxed">{costiIntro[lang]}</p>

      <div className="bg-terracotta-50 border border-terracotta-200 rounded-xl p-5 mt-4">
        <p className="font-serif text-base font-medium text-terracotta-500 mb-3">{labels.housing[lang]}</p>
        <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{costiCasa[lang]}</p>
      </div>
      <div className="bg-olive-50 border border-olive-200 rounded-xl p-5 mt-4">
        <p className="font-serif text-base font-medium text-olive-500 mb-3">{labels.rentals[lang]}</p>
        <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{costiAffitti[lang]}</p>
      </div>
      <div className="bg-ivory-100 border border-gold-200 rounded-xl p-5 mt-4">
        <p className="font-serif text-base font-medium text-gold-400 mb-3">{labels.utilities[lang]}</p>
        <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{costiUtenze[lang]}</p>
      </div>

      {/* ── Section 3: Sanità ── */}
      {sectionTitle('sanita-servizi', sanitaTitle[lang])}
      <div className="bg-olive-50 border border-olive-200 rounded-xl p-5 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-olive-100 flex-shrink-0 mt-0.5">
            <i className="ri-hospital-line text-olive-500" style={{ width: '16px', height: '16px', display: 'inline-block' }}></i>
          </div>
          <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{sanitaText[lang]}</p>
        </div>
      </div>

      {/* ── Section 4: Trasporti ── */}
      {sectionTitle('trasporti-mobilita', trasportiTitle[lang])}
      <div className="bg-terracotta-50 border border-terracotta-200 rounded-xl p-5 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-terracotta-100 flex-shrink-0 mt-0.5">
            <i className="ri-road-map-line text-terracotta-500" style={{ width: '16px', height: '16px', display: 'inline-block' }}></i>
          </div>
          <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{trasportiText[lang]}</p>
        </div>
      </div>

      {/* ── Section 5: Chi sta scegliendo le Marche ── */}
      {sectionTitle('chi-sceglie', chiTitle[lang])}
      <p className="font-sans text-base text-anthracite-500 leading-relaxed">{chiIntro[lang]}</p>

      <div className="bg-terracotta-50 border border-terracotta-200 rounded-xl p-5 mt-4">
        <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{chiNazionalita[lang]}</p>
      </div>

      <div className="bg-olive-50 border border-olive-200 rounded-xl p-5 mt-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-olive-100 flex-shrink-0 mt-0.5">
            <i className="ri-map-pin-2-line text-olive-500" style={{ width: '16px', height: '16px', display: 'inline-block' }}></i>
          </div>
          <p className="font-serif text-base font-medium text-olive-500">{labels.provinceLabel[lang]}</p>
        </div>
        <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{chiProvince[lang]}</p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {borgoLinks.map((c) => (
            <a
              key={c}
              href={`/borghi/${c.toLowerCase().replace(/\s+/g, '-')}`}
              className="inline-flex items-center justify-center gap-1.5 bg-white/70 border border-olive-200 rounded-full px-3 py-1.5 font-sans text-xs text-anthracite-600 hover:border-olive-400 hover:text-olive-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-pin-line text-olive-400" style={{ width: '12px', height: '12px', display: 'inline-block' }}></i>
              {c}
            </a>
          ))}
        </div>
      </div>

      <div className="bg-ivory-100 border border-gold-200 rounded-xl p-5 mt-4">
        <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{chiInterno[lang]}</p>
      </div>

      {/* ── Section 6: Punti di forza ── */}
      {sectionTitle('punti-di-forza', puntiTitle[lang])}
      <p className="font-sans text-base text-anthracite-500 leading-relaxed">{puntiIntro[lang]}</p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {puntiCards(lang).map((card) => (
          <div key={card.title} className={`rounded-xl p-5 ${accentClass(card.accent, 'bg')}`}>
            <div className="flex items-start gap-3 mb-2">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${accentClass(card.accent, 'circle')}`}>
                <i className={`${card.icon} ${accentClass(card.accent, 'text')}`} style={{ width: '16px', height: '16px', display: 'inline-block' }}></i>
              </div>
              <p className={`font-serif text-base font-medium ${accentClass(card.accent, 'text')}`}>{card.title}</p>
            </div>
            <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>

      {/* ── Section 7: Criticità ── */}
      {sectionTitle('criticità', criticitaTitle[lang])}
      <p className="font-sans text-base text-anthracite-500 leading-relaxed">{criticitaIntro[lang]}</p>

      <div className="space-y-4 mt-4">
        {criticitaCards(lang).map((card) => (
          <div key={card.title} className={`rounded-xl p-5 ${accentClass(card.accent, 'bg')}`}>
            <div className="flex items-start gap-3 mb-2">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${accentClass(card.accent, 'circle')}`}>
                <i className={`${card.icon} ${accentClass(card.accent, 'text')}`} style={{ width: '16px', height: '16px', display: 'inline-block' }}></i>
              </div>
              <p className={`font-serif text-base font-medium ${accentClass(card.accent, 'text')}`}>{card.title}</p>
            </div>
            <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>

      {/* ── Section 8: Opportunità ── */}
      {sectionTitle('opportunita', opportunitaTitle[lang])}
      <p className="font-sans text-base text-anthracite-500 leading-relaxed">{opportunitaIntro[lang]}</p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {opportunitaCards.map((card) => (
          <div key={card.title[lang]} className={`rounded-xl p-5 ${accentClass(card.accent, 'bg')}`}>
            <div className="flex items-start gap-3 mb-2">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${accentClass(card.accent, 'circle')}`}>
                <i className={`${card.icon} ${accentClass(card.accent, 'text')}`} style={{ width: '16px', height: '16px', display: 'inline-block' }}></i>
              </div>
              <p className={`font-serif text-base font-medium ${accentClass(card.accent, 'text')}`}>{card.title[lang]}</p>
            </div>
            <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{card.body[lang]}</p>
          </div>
        ))}
      </div>

      {/* ── Section 9: Conclusione ── */}
      {sectionTitle('conclusione', conclusioneTitle[lang])}

      <div className="bg-ivory-50 border border-ivory-200 rounded-xl p-6 mt-2 space-y-4">
        <p className="font-sans text-base text-anthracite-500 leading-relaxed">{conclusionePara1[lang]}</p>
        <p className="font-sans text-base text-anthracite-500 leading-relaxed">{conclusionePara2[lang]}</p>
        <p className="font-sans text-base text-anthracite-500 leading-relaxed">{conclusionePara3[lang]}</p>
        <p className="font-sans text-base text-anthracite-500 leading-relaxed">{conclusionePara4[lang]}</p>
        <div className="border-l-4 border-terracotta-300 pl-5 py-2">
          <p className="font-serif text-lg text-anthracite-700 italic leading-relaxed">{conclusioneQuote[lang]}</p>
        </div>
      </div>

      {/* ── Sources ── */}
      <div className="pt-8 border-t border-ivory-200">
        <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-3">{labels.fonti[lang]}</p>
        <p className="font-sans text-xs text-anthracite-400 leading-relaxed">{fonti[lang]}</p>
      </div>

      {/* ── Related borghi ── */}
      {relatedBorghiData.length > 0 && (
        <div className="mt-8 pt-8 border-t border-ivory-200">
          <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-6">{t('blog.borghi.correlati')}</p>
          <div className="flex flex-wrap gap-4">
            {relatedBorghiData.map((b) => (
              <a
                key={b.slug}
                href={`/borghi/${b.slug}`}
                className="group flex items-center gap-3 bg-ivory-50 border border-ivory-200 rounded-xl px-4 py-3 hover:border-terracotta-300 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="font-serif text-sm text-anthracite-700 group-hover:text-terracotta-300 transition-colors">{b.name}</p>
                  <p className="font-sans text-xs text-anthracite-400">{getBorgoTagline(b).substring(0, 40)}...</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VivereMarcheBody;